import { TextureSource } from '../../models/TextureSource';
import { ShaderProgram } from '../../utils/ShaderProgram';
import {
    Transformable,
    Transformation,
} from '../../../util/geometry/Transformation';
import { Renderer } from '../Renderer';
import { UniformMatrix } from '../../utils/uniforms/UniformMatrix';
import { RenderStorage } from '../RenderStorage';
import { gl } from '../../../Engine';
import { mat4 } from 'gl-matrix';
import { UniformFloat } from '../../utils/uniforms/UniformFloat';

/**
 * QuadRenderer offers rendering methods to render a single quad with texture content.
 */
export class QuadRenderer implements Renderer {
    /**
     * {@link QuadShader} that is used by the {@link Quadrenderer}.
     */
    private shader: QuadShader;
    private shader3D: QuadShader3D;

    /**
     * {@link QuadRenderer} constructor.\
     * This will initialize the {@link QuadShader}.
     */
    public constructor() {
        this.shader = new QuadShader();
        this.shader3D = new QuadShader3D();
    }

    /**
     * Method to render multiple {@link TextureSource} objects.\
     * Those objects may inherit from {@link Transformable} and contain a transformation.
     * @param textureSources Variadic array of {@link TextureSource} objects.
     */
    public render(...textureSources: TextureSource[]): boolean {
        //bind shader and VAO
        this.shader.start();
        const quadVAO = RenderStorage.getQuadVAO();
        quadVAO.bind(0);
        if (textureSources.length > 0) {
            for (const textureSource of textureSources) {
                if (textureSource.texture) {
                    const u: unknown = textureSource;
                    const transformation: Transformation | undefined = (<
                        Transformable
                    >u).transformation;
                    //if textureSource has transformation, we must use it for rendering, otherwise we use identity
                    this.shader.transformationMatrix.loadMatrix(
                        transformation
                            ? transformation.totalTransformation ||
                                  mat4.create()
                            : mat4.create()
                    );
                    gl.bindTexture(gl.TEXTURE_2D, textureSource.texture);
                    gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
                }
            }
        } else {
            this.shader.transformationMatrix.loadMatrix(mat4.create());
            gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
        }
        //unbind shader and VAO
        quadVAO.unbind(0);
        this.shader.stop();
        return true;
    }

    public render3D(k: number, ...textureSources: TextureSource[]): boolean {
        //bind shader and VAO
        this.shader3D.start();
        this.shader3D.k.loadFloat(k);
        const quadVAO = RenderStorage.getQuadVAO();
        quadVAO.bind(0);
        if (textureSources.length > 0) {
            for (const textureSource of textureSources) {
                if (textureSource.texture) {
                    const u: unknown = textureSource;
                    const transformation: Transformation | undefined = (<
                        Transformable
                    >u).transformation;
                    //if textureSource has transformation, we must use it for rendering, otherwise we use identity
                    this.shader3D.transformationMatrix.loadMatrix(
                        transformation
                            ? transformation.totalTransformation ||
                                  mat4.create()
                            : mat4.create()
                    );
                    gl.bindTexture(gl.TEXTURE_3D, textureSource.texture);
                    gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
                }
            }
        } else {
            this.shader3D.transformationMatrix.loadMatrix(mat4.create());
            gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
        }
        //unbind shader and VAO
        quadVAO.unbind(0);
        this.shader3D.stop();
        return true;
    }

    /**
     * Method to delete the {@link QuadShader} which is stored within this Renderer.
     */
    delete(): void {
        this.shader.delete();
        this.shader3D.delete();
    }
}

/**
 * The shader program used by {@link QuadRenderer}
 */
class QuadShader extends ShaderProgram {
    /**
     * QuadShader's vertex content.
     * Takes vec2 as an input parameter,
     * 4 by 4 transformation matrix as an uniform variable and
     * passes vec2 textureCoordinates to FRAGMENT_SHADER. These texture coordinates are in between 0 and 1.
     */
    private static readonly VERTEX_CONTENT = `#version 300 es

    precision highp float;

    in vec2 position;

    out vec2 pass_textureCoordinates;

    uniform mat4 transformation;

    void main() {
        gl_Position = transformation * vec4(position, 1.0, 1.0);
        pass_textureCoordinates = vec2((position.x + 1.0) * 0.5, (position.y + 1.0) * 0.5);
    }
    `;

    /**
     * QuadShader fragment content.
     * Gets vec2 textureCoordinates from VERTEX_SHADER,
     * uniform sampler2D as an uniform variable defining the texture to be rendered and
     * outputs vec4 color
     */
    private static readonly FRAGMENT_CONTENT = `#version 300 es
    
    precision highp float;
    
    in vec2 pass_textureCoordinates;

    out vec4 color;
    
    uniform sampler2D sampler;
    
    void main() {
        color = texture(sampler, pass_textureCoordinates);
    }`;

    /**
     * Matrix defining the transformation of points from [-1, 1] to view relative coordinates.
     * This should be 4 by 4 TransformationMatrix. Even though quads usually only need transformations on the 2D plane,
     * we don't want to limit the shader to just those. As 3D transformations might also have some use-cases.
     */
    public readonly transformationMatrix: UniformMatrix;

    /**
     * Constructor that creates QuadShader program, {@link QuadShader.transformationMatrix transformationMatrix}
     * -uniform variable and links those two.
     *
     * It is important to remember to delete the shader after no more used.
     */
    constructor() {
        super(
            QuadShader.VERTEX_CONTENT,
            QuadShader.FRAGMENT_CONTENT,
            'position'
        );
        this.transformationMatrix = new UniformMatrix('transformation');
        super.storeUniformLocations(this.transformationMatrix);
    }
}

/**
 * The shader program used by {@link QuadRenderer}
 */
class QuadShader3D extends ShaderProgram {
    /**
     * QuadShader's vertex content.
     * Takes vec2 as an input parameter,
     * 4 by 4 transformation matrix as an uniform variable and
     * passes vec2 textureCoordinates to FRAGMENT_SHADER. These texture coordinates are in between 0 and 1.
     */
    private static readonly VERTEX_CONTENT = `#version 300 es

    precision highp float;

    in vec2 position;

    out vec2 pass_textureCoordinates;

    uniform mat4 transformation;

    void main() {
        gl_Position = transformation * vec4(position, 1.0, 1.0);
        pass_textureCoordinates = vec2((position.x + 1.0) * 0.5, (position.y + 1.0) * 0.5);
    }
    `;

    /**
     * QuadShader fragment content.
     * Gets vec2 textureCoordinates from VERTEX_SHADER,
     * uniform sampler2D as an uniform variable defining the texture to be rendered and
     * outputs vec4 color
     */
    private static readonly FRAGMENT_CONTENT = `#version 300 es
    
    precision highp float;
    precision highp sampler3D;

    in vec2 pass_textureCoordinates;

    out vec4 color;
    
    uniform sampler3D sampler;
    
    uniform float k;

    void main() {
        color = texture(sampler, vec3(pass_textureCoordinates, k));
    }`;

    /**
     * Matrix defining the transformation of points from [-1, 1] to view relative coordinates.
     * This should be 4 by 4 TransformationMatrix. Even though quads usually only need transformations on the 2D plane,
     * we don't want to limit the shader to just those. As 3D transformations might also have some use-cases.
     */
    public readonly transformationMatrix: UniformMatrix;

    public readonly k: UniformFloat;

    /**
     * Constructor that creates QuadShader program, {@link QuadShader.transformationMatrix transformationMatrix}
     * -uniform variable and links those two.
     *
     * It is important to remember to delete the shader after no more used.
     */
    constructor() {
        super(
            QuadShader3D.VERTEX_CONTENT,
            QuadShader3D.FRAGMENT_CONTENT,
            'position'
        );
        this.transformationMatrix = new UniformMatrix('transformation');
        this.k = new UniformFloat('k');
        super.storeUniformLocations(this.transformationMatrix, this.k);
    }
}
